/**
 * Build/host-level feature switches.
 *
 * Kept separate from theme/brand.js: that module answers "how does this deployment LOOK",
 * this one answers "what does this deployment EXPOSE".
 *
 * IMPORTANT (Create React App): env vars are inlined at BUILD time and must be read as a
 * literal `process.env.REACT_APP_*` member expression — a dynamic lookup silently breaks
 * the substitution.
 */

/** Matches xnotifydemo.com and any subdomain of it (demo.xnotifydemo.com, …). */
const DEMO_HOST = /(^|\.)xnotifydemo\.com$/i;

function isDemoHost() {
  if (typeof window === 'undefined' || !window.location) return false;
  return DEMO_HOST.test(window.location.hostname || '');
}

/**
 * Tri-state env read: `null` when the var is absent or blank so the caller can apply its
 * own default, otherwise the parsed boolean. "false"/"0"/"no"/"off" are all falsey, since
 * a plain `Boolean("false")` is `true` and that mistake is easy to make in a .env.
 */
function envFlag(value) {
  const text = String(value == null ? '' : value).trim().toLowerCase();
  if (!text) return null;
  return !['false', '0', 'no', 'off'].includes(text);
}

/**
 * AI Insights (FEAT-0012 — the Campaign Report tab that runs the Campaign Performance
 * Analyst) must not be exposed on the public demo.
 *
 * `REACT_APP_ENABLE_AI_INSIGHTS=true|false` is the explicit control. Left unset it
 * defaults to ON everywhere EXCEPT the demo host, so demo.xnotifydemo.com hides the tab
 * with no .env change of its own, and every other deployment is unaffected.
 */
export const AI_INSIGHTS_ENABLED =true

/**
 * The Campaign Report "Impact" tab (FEAT-ATTRIB-003 A2), which reads
 * `xnotify-attribution`.
 *
 * Derived from whether the service has a base URL rather than from a flag of
 * its own, because a flag could be ON for a deployment that has no attribution
 * service — and CRA inlines an unset var as the literal string `"undefined"`,
 * so the tab would render and every request would 404 against a host that does
 * not exist. There is no useful state in which the tab is wanted and the URL
 * is absent, so one condition governs both.
 *
 * Read as a literal `process.env.REACT_APP_*` member expression, per this
 * file's own warning: a dynamic lookup silently breaks CRA's substitution.
 * `"undefined"` and `"null"` are rejected AS STRINGS, since `Boolean("undefined")`
 * is `true` and that is exactly what an unset var becomes.
 */
function attributionUrlConfigured() {
  const raw = process.env.REACT_APP_ATTRIBUTION_API_URL;
  if (!raw) return false;
  const v = String(raw).trim().toLowerCase();
  return v !== '' && v !== 'undefined' && v !== 'null';
}

export const ATTRIBUTION_ENABLED = attributionUrlConfigured();

const featureFlags = { AI_INSIGHTS_ENABLED, ATTRIBUTION_ENABLED };

export default featureFlags;
